import { useMemo } from 'preact/hooks';

import cx from 'clsx';

import {
	CardFilterType,
	CardMerchantSavedCard,
	ICardMerchantData,
	IMerchantCardData,
	MerchantCardSavedCard,
} from '@manage-cards/types';
import { formatDateDDMMYYYY } from '@manage-cards/utils/helpers';
import { Global, TEST_IDS, Text } from '@manage-cards/constants';

import { Button, Image } from '@shared/components/core';

import Dustbin from '@assets/icons/dustbinRed.svg';
import BankIcon from '@assets/icons/bankIcon.svg';

import DataTable, { DataColumn } from './DataTable';
import styles from './CardMerchantTable.module.scss';

interface ICardMerchantTableProps {
	tableData: ICardMerchantData | IMerchantCardData;
	onCardDelete: (cardId: string, merchantId: string) => void;
	filterType: CardFilterType;
	merchantId: string;
}

const CardMerchantTable = ({
	tableData,
	merchantId,
	filterType,
	onCardDelete,
}: ICardMerchantTableProps) => {
	const cardMerchantColumns: DataColumn<CardMerchantSavedCard>[] = useMemo(
		() => [
			{
				id: 'merchantName',
				header: Text.SELLER,
				align: 'left',
				render: (item: CardMerchantSavedCard) => (
					<div className={cx('d-flex items-center', styles.merchantCardName)}>
						<Image
							src={`${Global.MERCHANT_LOGO_BASE_URL}${item.merchantId}.png`}
							fallbackImage={Global.MERCHANT_LOGO_FALLBACK}
							alt="merchant"
							className={cx(
								styles.optionImg,
								'mr2 border-radius2 vertical-middle',
							)}
						/>
						<span className="h4 lighter dark3 truncate vertical-middle">
							{item.merchantName}
						</span>
					</div>
				),
			},
			{
				id: 'savedOn',
				header: Text.SAVED_ON,
				align: 'center',
				render: (item: CardMerchantSavedCard) => (
					<span className="h4 lighter dark3">
						{formatDateDDMMYYYY(item.savedOn)}
					</span>
				),
			},
			{
				id: 'delete',
				header: Text.DELETE,
				align: 'right',
				render: (item: CardMerchantSavedCard) => (
					<Button
						className={cx(
							styles.deleteButton,
							'd-inline-flex items-center justify-center',
						)}
						onClick={() => onCardDelete(item.cardId, item.merchantId)}
					>
						<Image src={Dustbin} alt="delete" />
					</Button>
				),
			},
		],
		[onCardDelete],
	);

	const merchantCardColumns: DataColumn<MerchantCardSavedCard>[] = useMemo(
		() => [
			{
				id: 'card',
				header: Text.CARD,
				align: 'left',
				render: (item: MerchantCardSavedCard) => (
					<div className={cx('d-flex items-center', styles.merchantCardName)}>
						<Image
							src={item.cardLogoUrl}
							fallbackImage={BankIcon}
							alt="card"
							className={cx(
								styles.optionImg,
								'mr2 border-radius2 vertical-middle',
							)}
						/>
						<span className="h4 lighter dark3 truncate vertical-middle">
							<span className={styles.bankName}>{item.bankName}</span> XX
							{item.lastFourDigits}
						</span>
					</div>
				),
			},
			{
				id: 'status',
				header: Text.STATUS,
				align: 'center',
				render: (item: MerchantCardSavedCard) => (
					<span
						className={cx(
							{ 'bg-warning color-warning': item.cardStatus === 'INACTIVE' },
							'px2 py1 border-radius1 h6 medium color-success bg-success',
						)}
					>
						{item.cardStatus}
					</span>
				),
			},
			{
				id: 'savedOn',
				header: Text.SAVED_ON,
				align: 'center',
				render: (item: MerchantCardSavedCard) =>
					formatDateDDMMYYYY(item.savedOn),
			},
			{
				id: 'delete',
				header: Text.DELETE,
				align: 'right',
				render: (item: MerchantCardSavedCard) => (
					<Button
						className={cx(
							styles.deleteButton,
							'd-inline-flex items-center justify-center',
						)}
						onClick={() => onCardDelete(item.cardId, merchantId)}
					>
						<Image src={Dustbin} alt="delete" />
					</Button>
				),
			},
		],
		[merchantId, onCardDelete],
	);

	return filterType === CardFilterType.BY_CARD ? (
		<DataTable
			id={TEST_IDS.CARD_MERCHANT_TABLE}
			className={styles.listTable}
			data={tableData.savedCards as CardMerchantSavedCard[]}
			columns={cardMerchantColumns}
		/>
	) : (
		<DataTable
			id={TEST_IDS.MERCHANT_CARD_TABLE}
			className={styles.listTable}
			data={tableData.savedCards as MerchantCardSavedCard[]}
			columns={merchantCardColumns}
		/>
	);
};

export default CardMerchantTable;
